import { useEffect } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  canonicalPath?: string;
  noindex?: boolean;
  lang?: string;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

export default function SEO({
  title,
  description,
  keywords,
  ogType = "website",
  ogImage,
  schema,
  canonicalPath,
  noindex,
  lang = "pt-BR",
}: SEOProps) {
  useEffect(() => {
    const fullTitle = title ? `${title} | GodoyProd` : "GodoyProd - Transcrição Profissional de Partituras Gospel";
    document.title = fullTitle;

    // Update html lang
    document.documentElement.lang = lang;

    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    if (description) setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);

    // Open Graph
    setMeta("og:title", fullTitle, "property");
    if (description) setMeta("og:description", description, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:url", canonicalPath ? `${SITE_URL}${canonicalPath}` : SITE_URL, "property");
    if (ogImage) setMeta("og:image", ogImage, "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    if (description) setMeta("twitter:description", description);
    if (ogImage) setMeta("twitter:image", ogImage);

    // Robots
    const robotsContent = noindex ? "noindex, nofollow" : "index, follow";
    setMeta("robots", robotsContent);

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.rel = "canonical";
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = canonicalPath ? `${SITE_URL}${canonicalPath}` : SITE_URL;

    // Last-modified
    let lastModEl = document.querySelector('meta[http-equiv="last-modified"]') as HTMLMetaElement | null;
    if (!lastModEl) {
      lastModEl = document.createElement("meta");
      lastModEl.setAttribute("http-equiv", "last-modified");
      document.head.appendChild(lastModEl);
    }
    lastModEl.content = new Date().toUTCString();

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo="jsonld"]');
    existingScripts.forEach((s) => s.remove());

    const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
    schemas.forEach((s) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "jsonld");
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
    });

    return () => {
      // cleanup JSON-LD only on unmount; keep meta for next page transition
      document.querySelectorAll('script[data-seo="jsonld"]').forEach((s) => s.remove());
    };
  }, [title, description, keywords, ogType, ogImage, schema, canonicalPath, noindex, lang]);

  return null;
}